// WEB-1991 (возврат живой приёмки 13.09) — ТОНКИЙ ПРЕДЗАГРУЗЧИК ТРЕЙСА.
//
// ЧТО СЛОМАЛОСЬ. Публичный вход поднимает трассировку ленивым импортом
// (`public-main.tsx` → `config/sentryPublicTracing.ts`), а роутер рисуется
// сразу — то есть САМЫЙ ПЕРВЫЙ запрос страницы, `GET /public/resolve`,
// уходит раньше, чем `@sentry/react` вообще загружен, и заголовков
// `sentry-trace`/`baggage` на нём нет. Трейс рвался на первом шаге: серверная
// половина резолва жила отдельным трейсом от всего остального.
//
// ПОЧЕМУ НЕ «ПОДНЯТЬ SDK РАНЬШЕ». Это упёрлось бы прямо в бюджет первого
// экрана публичной воронки (`build-guards/publicEntryGuard.ts`: цель 150 КБ
// gzip, сегодняшний потолок-храповик 270 КБ при факте ≈267 КБ). Даже
// «дождаться ленивого чанка перед резолвом» — плохой размен: показ воронки
// платному трафику ждал бы загрузки телеметрии.
//
// ЧТО ЗДЕСЬ. Полсотни строк без единого импорта SDK: свой идентификатор
// трейса, заголовок для первых запросов и мета-тег, из которого SDK ПРОДОЛЖИТ
// этот же трейс, когда догрузится. Вес первого экрана растёт на сотни байт, а
// не на десятки килобайт.
//
// СТЫК С SDK. `browserTracingIntegration` (@sentry/browser v10,
// `tracing/browserTracingIntegration.js`) в момент старта транзакции загрузки
// страницы читает `<meta name="sentry-trace">` (`getMetaContent`) и
// продолжает трейс из него. Мета-тег ставится синхронно при старте, то есть
// заведомо раньше ленивой инициализации, — поэтому браузерная и серверная
// половины оказываются в ОДНОМ трейсе, а не в двух.
//
// РЕШЕНИЕ О ВЫБОРКЕ ЗДЕСЬ НЕ ПРИНИМАЕТСЯ. В заголовке только две части
// (`<trace>-<span>`), без хвоста `-1`/`-0`. Хвост означал бы «родитель уже
// решил», и оба сэмплера обязаны были бы это решение уважать
// (`parentSampled`: `config/sentryPublicTracing.ts` на фронте,
// `shared/observability/trace-sampling.ts` на бэке) — то есть предзагрузчик,
// который про доли ничего не знает, молча отменил бы обе настройки выборки.
// Без хвоста каждый конец решает своей долей, как и задумано карточкой.
//
// БЕЗ DSN — ПОЛНЫЙ NO-OP: ни мета-тега, ни заголовков. Лишний заголовок на
// межсайтовом запросе стоит предварительного запроса CORS (`OPTIONS`), и
// платить за него там, где трассировки нет, незачем.
//
// ВТОРОЙ ЗАГОЛОВОК — `baggage` (ревизия пачки 32, Ф1). `sentry-trace` несёт
// только «кто родитель»; динамический контекст трейса (окружение, ключ
// проекта, сам trace_id) едет отдельным заголовком `baggage` — из него обе
// стороны узнают, к какому проекту и окружению относится трейс. Без него
// сшивание половин частичное: trace_id есть, а контекста нет.
// `sentry-sampled` в baggage предзагрузчик НЕ кладёт по той же причине, по
// которой не кладёт третью часть в `sentry-trace`: решение о выборке
// принимают сэмплеры, а не он.
import { SENTRY_DSN, SENTRY_ENVIRONMENT } from '@/config/sentry-config';

/** Имя заголовка и мета-тега — одно и то же слово протокола W3C/Sentry. */
const TRACE_HEADER = 'sentry-trace';

/** Динамический контекст трейса — тот же W3C `baggage`. */
const BAGGAGE_HEADER = 'baggage';

const TRACE_ID_BYTES = 16;
const SPAN_ID_BYTES = 8;

let traceHeaderValue: string | null = null;
let baggageHeaderValue: string | null = null;

/**
 * Случайные шестнадцатеричные знаки. `crypto.getRandomValues` — везде, где он
 * есть; `Math.random` — запасной путь для старых движков: идентификатор
 * трейса не секрет, его задача — не совпасть с соседним, а не устоять против
 * подбора.
 */
function randomHex(bytes: number): string {
  const cryptoApi = (
    globalThis as unknown as {
      crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array };
    }
  ).crypto;

  const buffer = new Uint8Array(bytes);
  if (typeof cryptoApi?.getRandomValues === 'function') {
    cryptoApi.getRandomValues(buffer);
  } else {
    for (let i = 0; i < bytes; i += 1) {
      buffer[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
}

/** Один мета-тег протокола: имя — слово протокола, содержимое — значение. */
function appendMeta(name: string, content: string): void {
  const meta = document.createElement('meta');
  meta.setAttribute('name', name);
  meta.setAttribute('content', content);
  document.head.appendChild(meta);
}

/**
 * Публичный ключ проекта из DSN (`https://<ключ>@хост/<проект>`).
 *
 * `null` — DSN непривычной формы: тогда в `baggage` просто не будет
 * `sentry-public_key`, остальные части поедут как есть. Ронять воронку из-за
 * разбора строки телеметрии нельзя — это тот же выбор, что у мягкой посадки
 * долей выборки в `config/sentryPublicTracing.ts`.
 */
function publicKeyFromDsn(dsn: string): string | null {
  try {
    const username = new URL(dsn).username;
    return username || null;
  } catch {
    return null;
  }
}

/**
 * Сборка значения `baggage`. Значения экранируются процентами: по W3C в
 * baggage нельзя сырыми запятые, точки с запятой и пробелы, а окружение —
 * произвольная строка из переменной сборки.
 */
function buildBaggage(traceId: string): string {
  const parts: string[] = [`sentry-trace_id=${traceId}`];

  if (SENTRY_ENVIRONMENT) {
    parts.push(`sentry-environment=${encodeURIComponent(SENTRY_ENVIRONMENT)}`);
  }

  const publicKey = SENTRY_DSN ? publicKeyFromDsn(SENTRY_DSN) : null;
  if (publicKey) {
    parts.push(`sentry-public_key=${encodeURIComponent(publicKey)}`);
  }

  return parts.join(',');
}

/**
 * Начать трейс страницы. Зовётся ОДИН раз из публичного входа — синхронно, до
 * первого рендера и до первого запроса.
 *
 * Повторный вызов ничего не перевыдаёт: трейс один на загрузку страницы,
 * иначе резолв и загрузка страницы разъехались бы по разным трейсам.
 */
export function startPublicTrace(): void {
  if (!SENTRY_DSN || traceHeaderValue) return;

  const traceId = randomHex(TRACE_ID_BYTES);
  traceHeaderValue = `${traceId}-${randomHex(SPAN_ID_BYTES)}`;
  baggageHeaderValue = buildBaggage(traceId);

  try {
    appendMeta(TRACE_HEADER, traceHeaderValue);
    appendMeta(BAGGAGE_HEADER, baggageHeaderValue);
  } catch {
    // Нет DOM (серверный рендер, необычная песочница) — заголовок всё равно
    // работает; продолжение трейса в SDK просто не состоится.
  }
}

/**
 * Заголовки трейса для запросов К НАШЕМУ API до инициализации SDK.
 *
 * Пустой объект — «трассировки нет» (нет DSN либо трейс не начат): вызывающий
 * тогда шлёт запрос ровно как раньше.
 *
 * ⚠ Только на наши адреса. Заголовок на чужом домене (Stripe, хранилище) —
 * это и утечка нашего идентификатора, и риск сломать сам запрос строгим CORS;
 * у SDK для этого есть `tracePropagationTargets`, здесь — ответственность
 * вызывающего.
 */
export function publicTraceHeaders(): Record<string, string> {
  if (!traceHeaderValue) return {};
  return {
    [TRACE_HEADER]: traceHeaderValue,
    ...(baggageHeaderValue ? { [BAGGAGE_HEADER]: baggageHeaderValue } : {}),
  };
}
